if (!customElements.get('nosht-auto-product-slider')) {
  class NoshtAutoProductSlider extends HTMLElement {
    constructor() {
      super();

      this.abortController = null;
      this.intersectionObserver = null;
      this.resizeObserver = null;
      this.reducedMotionQuery = null;
      this.finePointerQuery = null;
      this.animationFrame = null;
      this.endpointTimer = null;
      this.manualResumeTimer = null;
      this.position = 0;
      this.maxScroll = 0;
      this.direction = 1;
      this.currentVelocity = 0;
      this.velocityTransition = null;
      this.lastFrameTime = null;
      this.endpointPauseRemaining = 0;
      this.endpointPauseStartedAt = null;
      this.hasEnteredViewport = false;
      this.isIntersecting = false;
      this.isHovered = false;
      this.hasFocus = false;
      this.isManualInteraction = false;
      this.isTouchInteraction = false;
      this.isEditorSelected = false;
      this.isUserPaused = false;
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-auto-slider-viewport]');
      this.track = this.querySelector('[data-auto-slider-track]');
      this.toggleButton = this.querySelector('[data-auto-slider-toggle]');
      this.controlsOnlyHeader = this.querySelector('[data-auto-slider-controls-only]');

      if (!this.viewport || !this.track || this.abortController) return;

      this.speed = Math.max(0, Number.parseFloat(this.dataset.speed) || 28);
      this.endpointPause = Math.max(0, Number.parseInt(this.dataset.endpointPause, 10) || 4000);
      this.hoverEase = Math.max(0, Number.parseInt(this.dataset.hoverEase, 10) || 600);
      this.abortController = new AbortController();
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.position = this.viewport.scrollLeft;
      this.hasFocus = this.viewport.contains(document.activeElement);
      this.bindEvents();
      this.observeVisibility();
      this.observeSize();
      this.measure();
    }

    disconnectedCallback() {
      this.stopAnimation();
      this.clearEndpointTimer(false);
      window.clearTimeout(this.manualResumeTimer);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.toggleButton?.addEventListener('click', this.handleToggle, { signal });
      this.addEventListener('pointerenter', this.handlePointerEnter, { signal });
      this.addEventListener('pointerleave', this.handlePointerLeave, { signal });
      this.viewport.addEventListener('focusin', this.handleFocusIn, { signal });
      this.viewport.addEventListener('focusout', this.handleFocusOut, { signal });
      this.viewport.addEventListener('pointerdown', this.handlePointerDown, { signal });
      this.viewport.addEventListener('pointerup', this.handlePointerUp, { signal });
      this.viewport.addEventListener('pointercancel', this.handlePointerUp, { signal });
      this.viewport.addEventListener('wheel', this.handleWheel, { signal, passive: true });
      this.viewport.addEventListener('scroll', this.handleScroll, { signal, passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });
      document.addEventListener('shopify:section:select', this.handleSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect, { signal });
      this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange, { signal });
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.isIntersecting = true;
        this.hasEnteredViewport = true;
        this.updateMotion();
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.isIntersecting = Boolean(entry?.isIntersecting && entry.intersectionRatio > 0);

          if (this.isIntersecting) {
            this.hasEnteredViewport = true;
          } else if (this.isTouchInteraction) {
            this.isTouchInteraction = false;
            this.isManualInteraction = false;
          }

          this.updateMotion();
        },
        { threshold: [0, 0.05] },
      );

      this.intersectionObserver.observe(this);
    }

    observeSize() {
      if (!('ResizeObserver' in window)) return;

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.track);
    }

    measure() {
      const previousMax = this.maxScroll;
      this.maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      this.position = Math.min(this.maxScroll, Math.max(0, this.viewport.scrollLeft));

      if (previousMax > 0 && this.position >= previousMax - 1) {
        this.position = this.maxScroll;
        this.viewport.scrollLeft = this.maxScroll;
      }

      if (this.maxScroll <= 1) {
        this.direction = 1;
        this.position = 0;
        this.viewport.scrollLeft = 0;
      }

      this.updateMotion();
    }

    canAutoMove() {
      return (
        this.maxScroll > 1 &&
        this.hasEnteredViewport &&
        this.isIntersecting &&
        !document.hidden &&
        !this.reducedMotionQuery.matches &&
        !this.isUserPaused &&
        !this.hasFocus &&
        !this.isManualInteraction &&
        !this.isEditorSelected
      );
    }

    updateMotion() {
      this.updateToggle();

      if (this.maxScroll <= 1) {
        this.setState('static');
        this.currentVelocity = 0;
        this.stopAnimation();
        this.clearEndpointTimer();
        return;
      }

      if (this.reducedMotionQuery.matches) {
        this.setState('reduced-motion');
        this.hardPause();
        return;
      }

      if (!this.hasEnteredViewport) {
        this.setState('waiting');
        this.hardPause();
        return;
      }

      if (!this.canAutoMove()) {
        this.setState(this.pauseState());
        this.hardPause();
        return;
      }

      if (this.endpointPauseRemaining > 0) {
        this.startEndpointTimer();
        return;
      }

      if (this.isHovered) {
        this.setState('hover-stopping');
        this.transitionVelocity(0, this.hoverEase);
        return;
      }

      this.setState('moving');
      this.transitionVelocity(this.direction * this.speed, this.hoverEase);
    }

    pauseState() {
      if (this.isUserPaused) return 'user-paused';
      if (!this.isIntersecting || document.hidden) return 'offscreen-paused';
      if (this.isEditorSelected) return 'editor-paused';
      if (this.hasFocus) return 'focus-paused';
      if (this.isManualInteraction) return 'interaction-paused';
      return 'paused';
    }

    updateToggle() {
      if (!this.toggleButton) return;

      this.toggleButton.hidden = this.maxScroll <= 1 || this.reducedMotionQuery.matches;
      if (this.controlsOnlyHeader) this.controlsOnlyHeader.hidden = this.toggleButton.hidden;

      const action = this.isUserPaused ? 'play' : 'pause';
      const label = this.toggleButton.dataset[`${action}Label`] || '';
      const labelElement = this.toggleButton.querySelector('[data-auto-slider-toggle-label]');
      if (labelElement && labelElement.textContent !== label) labelElement.textContent = label;
      this.toggleButton.setAttribute('aria-label', this.toggleButton.dataset[`${action}Description`] || label);
      this.toggleButton.querySelectorAll('[data-auto-slider-toggle-icon]').forEach((icon) => {
        icon.hidden = icon.dataset.autoSliderToggleIcon !== action;
      });
    }

    handleToggle = () => {
      this.isUserPaused = !this.isUserPaused;
      window.clearTimeout(this.manualResumeTimer);
      this.manualResumeTimer = null;

      if (!this.isUserPaused) {
        this.isManualInteraction = false;
        this.isTouchInteraction = false;
        this.position = Math.min(this.maxScroll, Math.max(0, this.viewport.scrollLeft));
        if (this.position <= 1) this.direction = 1;
        if (this.position >= this.maxScroll - 1) this.direction = -1;
      }

      this.updateMotion();
    };

    transitionVelocity(target, duration) {
      const now = performance.now();

      if (this.velocityTransition?.target === target && this.animationFrame) return;

      this.velocityTransition = {
        from: this.currentVelocity,
        target,
        startedAt: now,
        duration,
      };
      this.lastFrameTime = now;
      this.ensureAnimation();
    }

    ensureAnimation() {
      if (this.animationFrame) return;
      this.animationFrame = window.requestAnimationFrame(this.tick);
    }

    stopAnimation() {
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.lastFrameTime = null;
      this.velocityTransition = null;
    }

    hardPause() {
      this.freezeEndpointTimer();
      this.currentVelocity = 0;
      this.stopAnimation();
    }

    tick = (now) => {
      this.animationFrame = null;

      if (!this.canAutoMove() || this.endpointPauseRemaining > 0) {
        this.currentVelocity = 0;
        this.lastFrameTime = null;
        return;
      }

      const delta = Math.min(64, Math.max(0, now - (this.lastFrameTime ?? now)));
      this.lastFrameTime = now;

      if (this.velocityTransition) {
        const elapsed = now - this.velocityTransition.startedAt;
        const progress = this.velocityTransition.duration > 0
          ? Math.min(1, elapsed / this.velocityTransition.duration)
          : 1;
        const eased = progress * progress * (3 - 2 * progress);

        this.currentVelocity = this.velocityTransition.from
          + (this.velocityTransition.target - this.velocityTransition.from) * eased;

        if (progress >= 1) {
          this.currentVelocity = this.velocityTransition.target;
          this.velocityTransition = null;
        }
      }

      if (Math.abs(this.currentVelocity) < 0.01) {
        this.currentVelocity = 0;

        if (this.velocityTransition && Math.abs(this.velocityTransition.target) >= 0.01) {
          this.ensureAnimation();
          return;
        }

        this.lastFrameTime = null;

        if (this.isHovered) this.setState('hover-paused');
        return;
      }

      const nextPosition = this.position + this.currentVelocity * (delta / 1000);

      if (this.direction > 0 && nextPosition >= this.maxScroll) {
        this.reachEndpoint(this.maxScroll, -1);
        return;
      }

      if (this.direction < 0 && nextPosition <= 0) {
        this.reachEndpoint(0, 1);
        return;
      }

      this.position = Math.min(this.maxScroll, Math.max(0, nextPosition));
      this.viewport.scrollLeft = this.position;
      this.ensureAnimation();
    };

    reachEndpoint(position, nextDirection) {
      this.position = position;
      this.viewport.scrollLeft = position;
      this.direction = nextDirection;
      this.currentVelocity = 0;
      this.stopAnimation();
      this.endpointPauseRemaining = this.endpointPause;
      this.startEndpointTimer();
    }

    startEndpointTimer() {
      if (this.endpointTimer || this.endpointPauseRemaining <= 0 || !this.canAutoMove() || this.isHovered) {
        if (this.endpointPauseRemaining > 0) this.setState('endpoint-paused');
        return;
      }

      this.setState('endpoint-paused');
      this.endpointPauseStartedAt = performance.now();
      this.endpointTimer = window.setTimeout(() => {
        this.endpointTimer = null;
        this.endpointPauseStartedAt = null;
        this.endpointPauseRemaining = 0;
        this.updateMotion();
      }, this.endpointPauseRemaining);
    }

    freezeEndpointTimer() {
      if (!this.endpointTimer || this.endpointPauseStartedAt === null) return;

      const elapsed = performance.now() - this.endpointPauseStartedAt;
      this.endpointPauseRemaining = Math.max(0, this.endpointPauseRemaining - elapsed);
      this.clearEndpointTimer(false);
    }

    clearEndpointTimer(resetRemaining = true) {
      if (this.endpointTimer) window.clearTimeout(this.endpointTimer);
      this.endpointTimer = null;
      this.endpointPauseStartedAt = null;
      if (resetRemaining) this.endpointPauseRemaining = 0;
    }

    scheduleManualResume() {
      window.clearTimeout(this.manualResumeTimer);
      this.manualResumeTimer = window.setTimeout(() => {
        this.isManualInteraction = false;
        this.position = Math.min(this.maxScroll, Math.max(0, this.viewport.scrollLeft));

        if (this.position <= 1) this.direction = 1;
        if (this.position >= this.maxScroll - 1) this.direction = -1;

        this.updateMotion();
      }, this.endpointPause);
    }

    setState(state) {
      if (this.dataset.sliderState !== state) this.dataset.sliderState = state;
    }

    handlePointerEnter = (event) => {
      if (!this.finePointerQuery.matches || event.pointerType === 'touch') return;

      this.isHovered = true;
      this.freezeEndpointTimer();
      this.updateMotion();
    };

    handlePointerLeave = (event) => {
      if (!this.finePointerQuery.matches || event.pointerType === 'touch') return;

      this.isHovered = false;
      this.updateMotion();
    };

    handleFocusIn = () => {
      this.hasFocus = true;
      this.updateMotion();
    };

    handleFocusOut = (event) => {
      if (event.relatedTarget && this.viewport.contains(event.relatedTarget)) return;

      this.hasFocus = false;
      this.updateMotion();
    };

    handlePointerDown = (event) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

      window.clearTimeout(this.manualResumeTimer);
      this.isManualInteraction = true;
      this.isTouchInteraction = true;
      this.updateMotion();
    };

    handlePointerUp = (event) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;

      this.position = Math.min(this.maxScroll, Math.max(0, this.viewport.scrollLeft));
      this.setState('interaction-paused');
    };

    handleWheel = () => {
      this.isTouchInteraction = false;
      this.isManualInteraction = true;
      this.updateMotion();
      this.scheduleManualResume();
    };

    handleScroll = () => {
      this.position = Math.min(this.maxScroll, Math.max(0, this.viewport.scrollLeft));
    };

    handleVisibilityChange = () => this.updateMotion();

    handleReducedMotionChange = () => this.updateMotion();

    handleSectionSelect = (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;

      this.isEditorSelected = true;
      this.updateMotion();
    };

    handleSectionDeselect = (event) => {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;

      this.isEditorSelected = false;
      this.updateMotion();
    };
  }

  customElements.define('nosht-auto-product-slider', NoshtAutoProductSlider);
}
